<template>
  <div>
    <div class="relative">
      <TextField
        class="pb-2"
        :inputId="'address2'"
        :showcharLimit="false"
        :inputext="location"
        :placholderText="`Search Address`"
        :lableText="'Search address'"
        :autoFocus="true"
        @inputChangeAction="val => location = val"
      />
      
      <!-- Autocomplete Dropdown -->
      <div
        v-if="isShowSelection && searchResults.length"
        class="card p-2 absolute z-10 bg-white w-full max-w shadow-lg"
        style="top: 100%;"
        v-click-outside="closeList"
      >
        <div
          v-for="(result) in searchResults"
          :key="result.place_id"
          @click="saveLocation(result)"
          class="flex hover:bg-gray-100 cursor-pointer py-2"
        >
          <div class="items-center grid w-14 mr-2">
            <div class="text-center">
              <i class="fas fa-map-marker-alt text-primary pr-3 pl-2"></i>
            </div>
            <div class="heading-7 text-center text-gray3">
              {{ getDistance(parseInt(result.distance_meters)) }}
            </div>
          </div>
          <div>
            <div class="cursor-pointer text-text1 font-semibold">
              {{ result.structured_formatting.main_text }}
            </div>
            <div class="cursor-pointer text-text2">
              {{ result.structured_formatting.secondary_text }}
            </div>
          </div>
        </div>
        <div v-if="searchResults.length > 1" class="dividerLight my-1"></div>
      </div>
    </div>

    <!-- Map -->
    <div class="py-4">
      <gmap-map
        v-if="addressObj.isShow"
        :center="addressObj.center"
        :zoom="18"
        @click="mark"
        style="width:100%; height: 350px;"
      >
        <gmap-marker :position="addressObj.center"></gmap-marker>
        <GmapCircle
          v-for="(pin, index) in markers"
          :key="index"
          :center="pin.position"
          :radius="addressObj.selectedRadius"
          :visible="true"
          :options="{ fillColor: '#ff000000', strokeColor: 'red', fillOpacity: 1.0 }"
        ></GmapCircle>
      </gmap-map>
      <div v-if="addressObj.isShow" class="pt-2">
        <p class="text-primary italic flex items-center">
          
          
          Click on the map to select or adjust the address location
        </p>
      </div>
    </div>

    <!-- Full Address Display -->
    <div v-if="addressObj.isShow" class="">
      <p v-if="addressObj.fullAddress" class="text-text2 heading-4" style="word-wrap: break-word; overflow-wrap: break-word; white-space: normal;">
        {{ addressObj.fullAddress }}
      </p>
    </div>
  </div>
</template>

<script>
/* global google */
import TextField from '@/View/components/textfield.vue'
import store from '@/store'
import Vue from 'vue'
import * as VueGoogleMaps from 'vue2-google-maps'
import GmapCircle from 'vue2-google-maps/dist/components/circle'

Vue.use(VueGoogleMaps, {
  load: {
    key: store.getters.googleMapApiKey,
    libraries: 'places,drawing'
  }
})

export default {
  components: { TextField, GmapCircle },
  props: ['isShowMap', 'showAdditional', 'selectedRadius', 'orgObj', 'clearSearch'],
  data: () => ({
    location: '',
    searchResults: [],
    isShowSelection: false,
    autocompleteService: null,
    placesService: null,
    biasCircle: null,        // <-- NEW: CircularGeofence for locationBias
    addressObj: {
      isShow: false,
      center: null,
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      zip: '',
      country: '',
      latitude: '',
      longitude: '',
      fullAddress: '',
      selectedRadius: 0
    },
    markers: [],
    debounceTimer: null,
    isSelectingPlace: false,
  }),

  watch: {
    location(newVal) {
      if (this.isSelectingPlace) return
      if (!newVal) {
        this.searchResults = []
        this.isShowSelection = false
        return
      }
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => this.performAutocomplete(newVal), 300)
    },
    clearSearch: {
      handler(val) {
        if (!val) return
        this.resetAll()
        this.$emit('update:clearSearch', false)
      },
      deep: true
    }
  },

  mounted() {
    this.$gmapApiPromiseLazy().then(() => {
      this.autocompleteService = new google.maps.places.AutocompleteService()
      this.placesService = new google.maps.places.PlacesService(
        new google.maps.Map(document.createElement('div'))
      )
      this.setBiasLocation()
    })

    if (this.orgObj) {
      this.location = this.orgObj.fullAddress
      this.addressObj.center = { lat: this.orgObj.latitude, lng: this.orgObj.longitude }
      this.addressObj.fullAddress = this.orgObj.fullAddress
      this.addressObj.isShow = true
    }

    this.addressObj.isShow = this.isShowMap
    this.$root.$on('radius', r => this.addressObj.selectedRadius = r)
  },

  beforeDestroy() {
    this.$root.$off('radius')
  },

  methods: {
    setBiasLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          pos => this.createBiasCircle(pos.coords.latitude, pos.coords.longitude),
          () => this.fallbackOrgLocation()
        )
      } else {
        this.fallbackOrgLocation()
      }
    },

    fallbackOrgLocation() {
      const org = JSON.parse(localStorage.getItem('orgInfo'))
      if (org?.latitude && org?.longitude) {
        this.createBiasCircle(org.latitude, org.longitude)
      }
    },

    // NEW: Create a CircularGeofence for locationBias
    createBiasCircle(lat, lng) {
      this.biasCircle = new google.maps.Circle({
        center: new google.maps.LatLng(lat, lng),
        radius: 50000  // 50 km – same as before
      })
    },

    performAutocomplete(input) {
      if (!this.autocompleteService || !input.trim()) return

      const request = {
        input,
        // NEW API: locationBias instead of location + radius
        fields: ["place_id", "structured_formatting"],
        ...(this.biasCircle && {
          locationBias: this.biasCircle
        })
      }

      this.autocompleteService.getPlacePredictions(request, async (predictions, status) => {
        if (status !== google.maps.places.PlacesServiceStatus.OK || !predictions) {
          this.searchResults = []
          this.isShowSelection = false
          return
        }

          const userLocation = this.biasCircle ? this.biasCircle.getCenter().toJSON() : null

          // Compute distances for each prediction
          this.searchResults = await Promise.all(
            predictions.map(prediction =>
              new Promise(resolve => {
                this.placesService.getDetails(
                  { placeId: prediction.place_id, fields: ["geometry"] },
                  (details, status) => {
                    let distance = null
                    if (
                      status === google.maps.places.PlacesServiceStatus.OK &&
                      details.geometry &&
                      userLocation
                    ) {
                      distance = this.computeDistance(userLocation, {
                        lat: details.geometry.location.lat(),
                        lng: details.geometry.location.lng()
                      })
                    }
                    resolve({ ...prediction, distance_meters: distance })
                  }
                )
              })
            )
          )
        
        this.isShowSelection = true
      })
    },

    computeDistance(a, b) {
      const R = 6371e3; // meters
      const toRad = x => (x * Math.PI) / 180;

      const dLat = toRad(b.lat - a.lat);
      const dLon = toRad(b.lng - a.lng);

      const lat1 = toRad(a.lat);
      const lat2 = toRad(b.lat);

      const x =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1) * Math.cos(lat2) *
        Math.sin(dLon / 2) * Math.sin(dLon / 2);

      return R * 2 * Math.atan2(Math.sqrt(x), Math.sqrt(1 - x));
    },


    async saveLocation(prediction) {
      this.isSelectingPlace = true
      this.isShowSelection = false
      this.searchResults = []

      try {
        const place = await this.getPlaceDetails(prediction.place_id)
        this.setPlace(place)
      } catch (e) {
        console.error('Place details error:', e)
      }
      setTimeout(() => {
        this.isSelectingPlace = false    // <-- re-enable watcher
      }, 100)
    },

    getPlaceDetails(placeId) {
      return new Promise((resolve, reject) => {
        this.placesService.getDetails({ placeId }, (result, status) => {
          if (status === google.maps.places.PlacesServiceStatus.OK) {
            resolve(result)
          } else {
            reject(status)
          }
        })
      })
    },

    setPlace(place) {
      this.markers = []
      this.addressObj.center = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng()
      }

      if (this.showAdditional) {
        this.markers.push({ position: this.addressObj.center })
      }

      this.addressObj.isShow = true
      this.addressObj.latitude = place.geometry.location.lat()
      this.addressObj.longitude = place.geometry.location.lng()
      this.addressObj.addressLine1 = place.name || ''
      this.addressObj.fullAddress = place.formatted_address

      // Parse address components
      for (const comp of place.address_components) {
        const type = comp.types[0]
        if (['street_number', 'route', 'sublocality', 'sublocality_level_1', 'sublocality_level_2'].includes(type)) {
          this.addressObj.addressLine1 += ' ' + comp.long_name
        }
        if (type === 'locality') this.addressObj.city = comp.long_name
        if (type === 'administrative_area_level_1') this.addressObj.state = comp.short_name
        if (type === 'administrative_area_level_2') this.addressObj.country = comp.long_name
        if (type === 'postal_code') this.addressObj.zip = comp.long_name
      }

      this.addressObj.addressLine1 = this.addressObj.addressLine1.trim()
      this.addressObj.fullAddress = `${place.name ? place.name + ', ' : ''}${place.formatted_address}`
      this.isSelectingPlace = true
      this.location = this.addressObj.fullAddress
      this.$nextTick(() => {
        this.isSelectingPlace = false
      })

      this.$root.$emit('GlobalAddressObject', this.addressObj)
    },

    mark(event) {
      const latlng = { lat: event.latLng.lat(), lng: event.latLng.lng() }
      const geocoder = new google.maps.Geocoder()

      geocoder.geocode({ location: latlng }, (results, status) => {
        let newAddress = ''
        if (status === 'OK' && results[1]) {
          newAddress = results[1].formatted_address
        }

        this.addressObj.center = latlng
        this.markers = this.showAdditional ? [{ position: latlng }] : []
        this.addressObj.fullAddress = newAddress
        this.location = newAddress
        this.addressObj.latitude = latlng.lat
        this.addressObj.longitude = latlng.lng
        this.$root.$emit('GlobalAddressObject', this.addressObj)
      })
    },

    getDistance(distanceMeter) {
      if (isNaN(distanceMeter)) return '--'
      const isUS = navigator.languages?.[0]?.startsWith('en-US') || false
      const miles = distanceMeter * 0.00062137
      const km = distanceMeter / 1000

      if (isUS) {
        return miles >= 1000 ? '999+ mi' : `${miles.toFixed(1)} mi`
      } else {
        return km >= 1000 ? '999+ km' : `${km.toFixed(1)} km`
      }
    },

    closeList() {
      this.isShowSelection = false
    },

    resetAll() {
      this.location = ''
      this.searchResults = []
      this.isShowSelection = false
      this.markers = []
      this.addressObj = {
        isShow: false,
        center: null,
        addressLine1: '',
        addressLine2: '',
        city: '',
        state: '',
        zip: '',
        country: '',
        latitude: '',
        longitude: '',
        fullAddress: '',
        selectedRadius: 0
      }
    }
  }
}
</script>

<style scoped>
.dividerLight { border-top: 1px solid #eee; }
</style>